/**
 * VersionPicker — shown when `/contracts/contracts` carries no `?versionId=`
 * (or an invalid one): Study -> Negotiation -> Version, the same chain
 * `WorkspaceOpener.tsx` (WP-55) uses for its own version selection, minus the
 * mode radio Workspace needs and this tab does not (session 4 DECISION a).
 * Picking a version is the tab's only navigation — it calls `onSelect`,
 * which the tab wires to `setSearchParams({ versionId })`.
 */
import { useEffect, useState } from 'react';
import { FileSignature } from 'lucide-react';
import PageHeader from '../../../ui/PageHeader';
import FilterSelect from '../../../ui/FilterSelect';
import EmptyState from '../../../ui/EmptyState';
import { primaryBtnCls } from '../../../ui/styles';
import { listStudies, listNegotiations } from '../../api/client';
import type { Study, Negotiation } from '../../api/client';
import { listNegotiationVersions } from '../../api/workspace';
import type { ContractsVersion } from '../../api/workspace';

interface VersionPickerProps {
  onSelect: (versionId: string) => void;
  /** The query string carried a `?versionId=` that was not a valid uuid — shown as a quiet notice rather than a doomed request (this WP's brief, the invalid-id test case). */
  invalid?: boolean;
}

export function VersionPicker({ onSelect, invalid = false }: VersionPickerProps) {
  const [studies, setStudies] = useState<Study[]>([]);
  const [studyId, setStudyId] = useState('');
  const [negotiations, setNegotiations] = useState<Negotiation[]>([]);
  const [negotiationId, setNegotiationId] = useState('');
  const [versions, setVersions] = useState<ContractsVersion[]>([]);
  const [versionId, setVersionId] = useState('');

  useEffect(() => {
    listStudies().then(setStudies).catch(() => setStudies([]));
  }, []);

  useEffect(() => {
    if (!studyId) {
      setNegotiations([]);
      return;
    }
    listNegotiations(studyId).then(setNegotiations).catch(() => setNegotiations([]));
  }, [studyId]);

  useEffect(() => {
    if (!negotiationId) {
      setVersions([]);
      return;
    }
    listNegotiationVersions(negotiationId).then(setVersions).catch(() => setVersions([]));
  }, [negotiationId]);

  return (
    <div className="mx-auto w-full max-w-[1700px] space-y-4 p-4 sm:p-6 lg:p-8">
      <PageHeader
        title="Contracts"
        icon={FileSignature}
        subtitle="Clause list with flags and similarity matches, position shown beside each flagged clause, apply clause language from a previous study, decisions, redline export."
      />

      {invalid && (
        <p className="text-sm text-amber-600 dark:text-amber-400">
          The version in the link was not a valid id — pick one below.
        </p>
      )}

      <div data-testid="ctr-picker" className="flex flex-wrap items-end gap-2">
        <FilterSelect
          label="Study"
          auto
          value={studyId}
          onChange={(v) => {
            setStudyId(v);
            setNegotiationId('');
            setVersionId('');
          }}
          options={[{ value: '', label: 'Choose a study…' }, ...studies.map((s) => ({ value: s.id, label: s.protocol_number }))]}
        />
        <FilterSelect
          label="Negotiation"
          auto
          disabled={!studyId}
          value={negotiationId}
          onChange={(v) => {
            setNegotiationId(v);
            setVersionId('');
          }}
          options={[
            { value: '', label: 'Choose a negotiation…' },
            ...negotiations.map((n) => ({ value: n.id, label: n.label ?? n.kind })),
          ]}
        />
        <FilterSelect
          label="Version"
          auto
          disabled={!negotiationId}
          value={versionId}
          onChange={setVersionId}
          options={[
            { value: '', label: 'Choose a version…' },
            ...versions.map((v) => ({ value: v.id, label: `#${v.version_no} — ${v.direction} — ${v.version_date}` })),
          ]}
        />
        <button
          type="button"
          data-testid="ctr-picker-open"
          className={primaryBtnCls}
          disabled={!versionId}
          onClick={() => onSelect(versionId)}
        >
          Open
        </button>
      </div>

      {negotiationId && versions.length === 0 && (
        <EmptyState
          icon={FileSignature}
          title="No versions yet"
          hint="This negotiation has no documents to review."
          variant="card"
        />
      )}
    </div>
  );
}

export default VersionPicker;
